import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';

const UploadImage = () => {
  const authState = useSelector((state) => state.authReducer);
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState(true);
  const [errorMessage, setErrorMessage] = useState(''); 
  const [successMessage, setSuccessMessage] = useState(''); 
  const [uploadSuccess, setUploadSuccess] = useState(false);

  // Handle file change
  const onFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
    setErrorMessage(''); 
  };

  // Generate a unique upload control number
  const generateUploadControlNumber = () => {
    const currentDateTime = new Date();
    const dd = String(currentDateTime.getDate()).padStart(2, '0');
    const mm = String(currentDateTime.getMonth() + 1).padStart(2, '0');
    const yy = String(currentDateTime.getFullYear()).substring(2);
    const hh = String(currentDateTime.getHours()).padStart(2, '0');
    const mi = String(currentDateTime.getMinutes()).padStart(2, '0');
    const ss = String(currentDateTime.getSeconds()).padStart(2, '0');

    return `${authState.user?.think5whyscode || ''}${dd}${mm}${yy}${hh}${mi}${ss}`;
  };

  const validateFile = () => {
    const allowedTypes = [
      // Image types
      'image/png',
      'image/jpg',
      'image/jpeg',
      'image/tiff',
  
      // Document types
      'application/pdf',
      'application/json',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  
      // Custom application type
      'application/t5w',
  
      // Audio types
      'audio/mpeg',  // MP3
      'audio/aac',
      'audio/wav',
      'audio/ogg',
  
      // Video types
      'video/mp3',
      'video/mp4',
      'video/webm',
      'video/avi',
      'video/quicktime', // MOV
      'video/3gpp'  // 3GP for mobile
    ];
  
    if (!selectedFile) {
      setErrorMessage('Please select a file');
      return false;
    }
  
    if (!allowedTypes.includes(selectedFile.type)) {
      setErrorMessage('Invalid file type');
      return false;
    }
  
    // Check if the file is a video and adjust size validation
      const isVideo = selectedFile.type.startsWith('video/');
      const maxSize = isVideo ? 10 * 1024 * 1024 : 1 * 1024 * 1024; // 10MB for video, 1MB for others

      if (selectedFile.size > maxSize) {
        setErrorMessage(`File size exceeds ${isVideo ? '10MB' : '1MB'}`);
        return false;
      }

      return true;
  };
  

  const onFileUpload = async () => {
    if (title.trim() === '') {
      setErrorMessage('Title is required');
      return;
    }

    if (!validateFile()) return;

    setIsUploading(true);
    setErrorMessage(''); 

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('title', title);
    formData.append('description', description);
    formData.append('status', status);
    formData.append('userId', authState.user._id);
    formData.append('userName', authState.user.name);
    formData.append('userCode', authState.user.think5whyscode);
    formData.append('fileType', selectedFile.type);
    formData.append('fileSize', selectedFile.size);
    formData.append('dateCreated', new Date());
    formData.append('createdBy', authState.user.name);
    formData.append('uploadControlNumber', generateUploadControlNumber());

    try {
      const response = await axios.post('/api/uploads/upload-files', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setSuccessMessage('File uploaded successfully!');
      setUploadSuccess(true);
      setSelectedFile('');
    } catch (error) {
      console.error('Error uploading file:', error);
      setErrorMessage('Failed to upload file');
      setUploadSuccess(false);
    } finally {
      setIsUploading(false);
      setUploadSuccess(false);
    }
  };

  const onUploadAnother = () => {
    setUploadSuccess(false);
    setSelectedFile(null);
    setTitle('');
    setDescription('');
    setStatus(true);
    setIsUploading(false);
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleClose = () => {
    navigate(-1); 
  };

  return (
    <div className="bg-gray-800 p-6 rounded-lg shadow-md mb-8 max-w-4xl mx-auto">
      <h2>Upload File</h2>

      <div className="mb-4">
        <label className="block mb-1">Title (Max 100 characters)</label>
        <input
          type="text"
          maxLength={100}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="border rounded px-2 py-1 w-full bg-white text-black"
          style={{ width: '90%' }}
        />
      </div>

      <div className="mb-4">
        <label className="block mb-1">Description (Max 500 characters)</label>
        <textarea
          maxLength={500}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="border rounded px-2 py-1 w-full text-black bg-white"
          style={{ height: '150px', width: '90%' }}
        />
      </div>

      <div className="mb-4 text-white">
        <input type="file" onChange={onFileChange} />
      </div>

      {errorMessage && (
        <div className="mb-4 text-red-500">
          {errorMessage}
        </div>
      )}
       {successMessage && (
        <div className="mb-4 text-green-500">
          {successMessage}
        </div>
      )}
           
      <button
        onClick={onFileUpload}
        disabled={isUploading || successMessage}
        className={`mt-4 w-40 bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-full transition duration-300 ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {isUploading ? 'Uploading...' : 'Upload'}
      </button>
      <button
        onClick={onUploadAnother}
        disabled={!successMessage} 
        className="mt-4 w-40 bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-full transition duration-300"
        >
        Upload Another
      </button>

      <button
          className="mt-4 w-20 bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-4 rounded-full transition duration-300"
          onClick={handleClose}
        >
          Close
        </button>
    </div>
  );
};

export default UploadImage;
